import axios from 'axios'
import Vue from 'vue'

/**
 * Centralized API Service
 * Handles all API calls with proper authentication, base URL configuration, and error handling
 */
class ApiService {
  constructor() {
    this.instance = null
    this.initializeAxiosInstance()
  }

  /**
   * Get the correct API base URL based on environment
   * @returns {string} The API base URL
   */
  getBaseURL() {
    // Use the configured expressApi if available (for localhost), otherwise use the default api
    const baseURL = Vue.prototype.$expressApi || process.env.API
    console.log('API Service using base URL:', baseURL)
    return baseURL
  }

  /**
   * Initialize axios instance with interceptors
   */
  initializeAxiosInstance() {
    this.instance = axios.create({
      timeout: 30000, // 30 seconds timeout
    })

    // Request interceptor - set baseURL dynamically and add auth token and common headers
    this.instance.interceptors.request.use(
      (config) => {
        // Set the baseURL dynamically when the request is made
        if (!config.baseURL) {
          config.baseURL = this.getBaseURL()
        }

        const token = window.localStorage.getItem('default-auth-token')
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`
        }
        config.headers['Accept'] = 'application/json'
        config.headers['Content-Type'] = 'application/json'
        
        console.log(`API Request: ${config.method ? config.method.toUpperCase() : 'UNKNOWN'} ${config.baseURL}${config.url}`)
        return config
      },
      (error) => {
        console.error('API Request Error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor - handle common response scenarios
    this.instance.interceptors.response.use(
      (response) => {
        console.log(`API Response: ${response.status} ${response.config.method ? response.config.method.toUpperCase() : 'UNKNOWN'} ${response.config.url}`)
        return response
      },
      (error) => {
        console.error('API Response Error:', error.response ? error.response.status : 'No status', error.response ? error.response.data : error.message)
        
        // Handle common error scenarios
        if (error.response && error.response.status === 401) {
          console.warn('Unauthorized access - token may be invalid or expired')
          // Could trigger logout or token refresh here
        } else if (error.response && error.response.status === 403) {
          console.warn('Forbidden access - insufficient permissions')
        } else if (error.response && error.response.status >= 500) {
          console.error('Server error - please try again later')
        }
        
        return Promise.reject(error)
      }
    )
  }

  /**
   * Reinitialize the axios instance (useful when base URL changes)
   */
  reinitialize() {
    this.initializeAxiosInstance()
  }

  /**
   * Generic GET request
   * @param {string} url - The endpoint URL
   * @param {Object} config - Additional axios config
   * @returns {Promise} Axios response promise
   */
  async get(url, config = {}) {
    return this.instance.get(url, config)
  }

  /**
   * Generic POST request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request payload
   * @param {Object} config - Additional axios config
   * @returns {Promise} Axios response promise
   */
  async post(url, data = {}, config = {}) {
    return this.instance.post(url, data, config)
  }

  /**
   * Generic PUT request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request payload
   * @param {Object} config - Additional axios config
   * @returns {Promise} Axios response promise
   */
  async put(url, data = {}, config = {}) {
    return this.instance.put(url, data, config)
  }

  /**
   * Generic DELETE request
   * @param {string} url - The endpoint URL
   * @param {Object} config - Additional axios config
   * @returns {Promise} Axios response promise
   */
  async delete(url, config = {}) {
    return this.instance.delete(url, config)
  }

  /**
   * Generic PATCH request
   * @param {string} url - The endpoint URL
   * @param {Object} data - Request payload
   * @param {Object} config - Additional axios config
   * @returns {Promise} Axios response promise
   */
  async patch(url, data = {}, config = {}) {
    return this.instance.patch(url, data, config)
  }

  /**
   * Upload file with multipart/form-data
   * @param {string} url - The endpoint URL
   * @param {FormData} formData - Form data with file
   * @param {Function} onUploadProgress - Progress callback
   * @returns {Promise} Axios response promise
   */
  async uploadFile(url, formData, onUploadProgress = null) {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }

    if (onUploadProgress) {
      config.onUploadProgress = onUploadProgress
    }

    return this.instance.post(url, formData, config)
  }

  /**
   * Get current base URL
   * @returns {string} Current base URL
   */
  getCurrentBaseURL() {
    return this.getBaseURL()
  }

  /**
   * Update authorization token
   * @param {string} token - New authorization token
   */
  setAuthToken(token) {
    if (token) {
      window.localStorage.setItem('default-auth-token', token)
    } else {
      window.localStorage.removeItem('default-auth-token')
    }
    // Reinitialize to pick up the new token
    this.reinitialize()
  }
}

// Create and export a singleton instance
const apiService = new ApiService()

export default apiService



// WEBPACK FOOTER //
// ./src/services/apiService.js